import React from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Star, Palette, Sparkles } from 'lucide-react';
const CircleCheck = ({ className }) => (<svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden={true}>
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>);
export const PricingCard = ({ name, price, priceHint, annualNote, description, featuresLabel, items, cta, disableCta, onCtaClick, highlight, footerNote, themesBadge, }) => {
    return (<motion.div className={cn('relative flex h-full flex-col rounded-2xl p-6', highlight
            ? 'bg-primary text-primary-foreground shadow-2xl shadow-primary/30'
            : 'border bg-card transition-all duration-200 hover:border-primary/30 hover:shadow-lg')} whileHover={{ y: -4, transition: { duration: 0.18, ease: 'easeOut' } }}>
            {/* Inner decorative glows — overflow-hidden scoped here so the badge isn't clipped */}
            {highlight && (<div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
                    <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary-foreground/10 blur-3xl"/>
                    <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-primary-foreground/5 blur-3xl"/>
                </div>)}

            {/* Most Popular badge */}
            {highlight && (<div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-foreground px-3 py-1 text-[11px] font-semibold text-primary shadow-md">
                        <Star className="h-3 w-3 fill-primary"/>
                        Most Popular
                    </span>
                </div>)}

            {/* Header */}
            <div className="mb-5">
                <h3 className={cn('text-xs font-semibold uppercase tracking-widest', highlight ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                    {name}
                </h3>

                <div className="mt-3 flex items-baseline gap-1.5">
                    <span className={cn('font-extrabold tracking-tight', highlight ? 'text-5xl' : 'text-4xl')}>
                        {price}
                    </span>
                    {priceHint && (<span className={cn('text-xs', highlight ? 'text-primary-foreground/60' : 'text-muted-foreground')}>
                            {priceHint}
                        </span>)}
                </div>

                {annualNote && (<p className={cn('mt-1 text-[11px] font-medium', highlight ? 'text-primary-foreground/70' : 'text-primary')}>
                        {annualNote}
                    </p>)}

                <p className={cn('mt-2 text-xs leading-relaxed', highlight ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                    {description}
                </p>
            </div>

            {/* Divider */}
            <div className={cn('mb-4 h-px', highlight ? 'bg-primary-foreground/20' : 'bg-border')}/>

            {/* Themes & animations badge — visual reminder that the design
            system unlocks at this tier. Sits above the feature list so
            it's the first thing the eye lands on after the price. */}
            {themesBadge && (<div className={cn('mb-4 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[11px] font-medium', highlight
                ? 'bg-primary-foreground/10 text-primary-foreground'
                : 'bg-primary/5 text-foreground')}>
                    <span className="flex items-center gap-1.5">
                        <Palette className={cn('h-3.5 w-3.5', highlight ? 'text-primary-foreground/80' : 'text-primary')}/>
                        <span className="font-bold tabular-nums">{themesBadge.themes}</span>
                        <span className={cn('uppercase tracking-wider text-[10px]', highlight ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                            themes
                        </span>
                    </span>
                    <span className={cn('h-3 w-px', highlight ? 'bg-primary-foreground/30' : 'bg-border')}/>
                    <span className="flex items-center gap-1.5">
                        <Sparkles className={cn('h-3.5 w-3.5', highlight ? 'text-primary-foreground/80' : 'text-primary')}/>
                        <span className="font-bold tabular-nums">{themesBadge.animations}</span>
                        <span className={cn('uppercase tracking-wider text-[10px]', highlight ? 'text-primary-foreground/70' : 'text-muted-foreground')}>
                            animations
                        </span>
                    </span>
                </div>)}

            {/* Features label */}
            {featuresLabel && (<p className={cn('mb-3 text-[10px] font-semibold uppercase tracking-wider', highlight ? 'text-primary-foreground/50' : 'text-muted-foreground')}>
                    {featuresLabel}
                </p>)}

            {/* Features */}
            <ul className="mb-6 flex-1 space-y-3">
                {items.map((item) => (<li key={item} className="flex items-start gap-2.5 text-xs">
                        <CircleCheck className={cn('mt-0.5 h-4 w-4 shrink-0', highlight ? 'text-primary-foreground/80' : 'text-primary')}/>
                        <span className={cn(highlight ? 'text-primary-foreground/85' : '')}>{item}</span>
                    </li>))}
            </ul>

            {/* CTA */}
            <div className="mt-auto">
                {onCtaClick ? (<Button disabled={disableCta} className={cn('w-full rounded-xl text-xs font-semibold', highlight ? 'bg-primary-foreground text-primary hover:bg-primary-foreground/90' : '')} onClick={onCtaClick}>
                        {cta}
                    </Button>) : (<Link to="/sign-up" className={cn('inline-flex w-full items-center justify-center rounded-xl px-4 py-3 text-xs font-semibold transition-all duration-200', highlight
                ? 'bg-primary-foreground text-primary shadow-sm hover:bg-primary-foreground/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90', disableCta && 'pointer-events-none opacity-40')}>
                        {cta}
                    </Link>)}

                {footerNote && (<p className={cn('mt-2 text-center text-[10px]', highlight ? 'text-primary-foreground/50' : 'text-muted-foreground')}>
                        {footerNote}
                    </p>)}
            </div>
        </motion.div>);
};
PricingCard.displayName = 'PricingCard';
